import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { useApp } from "../../../../context/AppContext";
import { useTransaction } from "../../../../context/TransactionContext";
import { CalendarEventType } from "../../../../types/calendar";
import { transformTransactionsToEvents } from "../../../../utils/transactionUtils";
import {
  getMonthBufferDates,
  mapTimeBlocksToEvent,
  mergeFetchedEvents,
} from "../calendarEventUtils";
import {
  CalendarBusinessInfo,
  useTypedCalendarEvent,
} from "../calendarViewTypes";

interface UseCalendarDataArgs {
  businessInfo: CalendarBusinessInfo | null;
  isMissingSalon: boolean | null;
  salonServices: any[];
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  dateRange: { start: string; end: string };
}

export type FetchAppointmentsAndTimeBlocks = (
  customStartDate?: string,
  customEndDate?: string,
  monthsToMark?: string[],
  options?: { replaceAll?: boolean },
) => Promise<void>;

/**
 * Appointment + time-block fetching with a month cache: initial 4-month load,
 * predictive fetch on month navigation, and the merge-with-eviction path used
 * by every background refetch.
 */
export function useCalendarData({
  businessInfo,
  isMissingSalon,
  salonServices,
  selectedDate,
  setSelectedDate,
  dateRange,
}: UseCalendarDataArgs) {
  const { getTimeBlocks } = useApp();
  const { fetchTransactions } = useTransaction();
  const { setEvents, fetchedMonths, setFetchedMonths } =
    useTypedCalendarEvent();

  // False when fetchedMonths is already populated (remount after token refresh)
  // so the LoadingOverlay isn't shown when performInitialFetch is skipped.
  const [isInitialLoading, setIsInitialLoading] = useState(
    fetchedMonths.size === 0,
  );

  // Track last selected month for predictive fetching
  // Initialize with today's month, not selectedDate, to match performInitialFetch
  const [lastSelectedMonth, setLastSelectedMonth] = useState<string>(
    moment().format("YYYY-MM"),
  );

  // Seeded from context so a remount after token refresh doesn't re-run the
  // initial fetch and wipe displayed appointments with replaceAll: true.
  const hasInitialFetchCompletedRef = useRef(fetchedMonths.size > 0);

  // dateRange / salonServices are read through refs so the fetcher's identity
  // doesn't churn (and re-trigger the initial/predictive effects) on every
  // navigation or services refresh.
  const dateRangeRef = useRef(dateRange);
  useEffect(() => {
    dateRangeRef.current = dateRange;
  }, [dateRange]);
  const salonServicesRef = useRef(salonServices);
  useEffect(() => {
    salonServicesRef.current = salonServices;
  }, [salonServices]);

  // Reset selectedDate to today when Calendar mounts (user starts fresh on
  // return). Guarded by a ref so it only fires on the first run.
  const didResetDateRef = useRef(false);
  useEffect(() => {
    if (didResetDateRef.current) return;
    didResetDateRef.current = true;
    const today = new Date();
    const todayStr = moment(today).format("YYYY-MM-DD");
    const selectedDateStr = moment(selectedDate).format("YYYY-MM-DD");

    // Only reset if selectedDate is not today (avoids unnecessary updates)
    if (todayStr !== selectedDateStr) {
      setSelectedDate(today);
      setLastSelectedMonth(moment(today).format("YYYY-MM"));
    }
  }, [selectedDate, setSelectedDate]);

  const timezone = businessInfo?.timezone;
  const mapTimeBlocks = useCallback(
    (timeBlocks: any[], staffId?: string) =>
      mapTimeBlocksToEvent(timeBlocks, timezone, staffId),
    [timezone],
  );

  // Fetch appointments and time blocks with smart caching and merging
  const fetchAppointmentsAndTimeBlocks =
    useCallback<FetchAppointmentsAndTimeBlocks>(
      async (customStartDate, customEndDate, monthsToMark, options = {}) => {
        try {
          if (!businessInfo?.id) return;

          const { replaceAll = false } = options;

          // Use custom dates if provided, otherwise fall back to dateRange from
          // ref (the time_off sync refetch uses the currently-viewed range).
          const startDate = customStartDate || dateRangeRef.current.start;
          const endDate = customEndDate || dateRangeRef.current.end;
          const queryParams = `start_date=${startDate}&end_date=${endDate}`;

          // Fetch transactions and time blocks in parallel. Use allSettled so
          // that a failed timeoff request (e.g. 403) does not prevent
          // appointments from showing.
          const [transactionsResult, timeBlocksResult] =
            await Promise.allSettled([
              fetchTransactions(businessInfo.id, queryParams),
              getTimeBlocks(businessInfo.id, queryParams),
            ]);

          const transactionsResponse =
            transactionsResult.status === "fulfilled"
              ? transactionsResult.value
              : null;
          const timeBlocksResponse =
            timeBlocksResult.status === "fulfilled"
              ? timeBlocksResult.value
              : null;

          if (transactionsResult.status === "rejected") {
            console.error(
              "Error fetching transactions:",
              transactionsResult.reason,
            );
          }
          if (timeBlocksResult.status === "rejected") {
            console.error(
              "Error fetching time off (appointments still shown):",
              timeBlocksResult.reason,
            );
          }

          const transformedEvents = transactionsResponse
            ? transformTransactionsToEvents(
                transactionsResponse,
                businessInfo.timezone,
                salonServicesRef.current,
              )
            : [];

          const transformedTimeBlocks = timeBlocksResponse?.records
            ? mapTimeBlocksToEvent(
                timeBlocksResponse.records,
                businessInfo.timezone,
              )
            : [];

          const newEvents = [
            ...transformedEvents,
            ...transformedTimeBlocks,
          ] as CalendarEventType[];

          if (replaceAll) {
            // Replace all events (used for initial load and full refresh)
            setEvents(newEvents);
          } else {
            setEvents((prevEvents) =>
              mergeFetchedEvents(
                prevEvents,
                newEvents,
                { startDate, endDate },
                {
                  transactions: transactionsResponse !== null,
                  timeBlocks: timeBlocksResponse !== null,
                },
              ),
            );
          }

          // Mark months as fetched
          if (monthsToMark) {
            setFetchedMonths((prev) => {
              const newSet = new Set(prev);
              monthsToMark.forEach((month) => newSet.add(month));
              return newSet;
            });
          }
        } catch (error) {
          console.error("Error fetching calendar data:", error);
        }
      },
      [
        businessInfo,
        fetchTransactions,
        getTimeBlocks,
        setEvents,
        setFetchedMonths,
      ],
    );

  // Initial fetch - Load previous month, current month, and next 2 months
  const performInitialFetch = useCallback(async () => {
    if (!businessInfo?.id || hasInitialFetchCompletedRef.current) return;

    const { start, end, months } = getMonthBufferDates(new Date());
    // Initial fetch: shows loading overlay and replaces all data
    await fetchAppointmentsAndTimeBlocks(start, end, months, {
      replaceAll: true,
    });
    hasInitialFetchCompletedRef.current = true;
    setIsInitialLoading(false);
  }, [businessInfo?.id, fetchAppointmentsAndTimeBlocks]);

  // Background-fetch the month buffer around `date` unless every month in it
  // is already cached (returns immediately with no API call then).
  const performPredictiveFetch = useCallback(
    async (newDate: Date) => {
      if (!businessInfo?.id) return;

      const { start, end, months } = getMonthBufferDates(newDate);
      const monthsToFetch = months.filter((month) => !fetchedMonths.has(month));
      if (monthsToFetch.length > 0) {
        // Background fetch: no loading overlay, merge with existing data
        await fetchAppointmentsAndTimeBlocks(start, end, months, {
          replaceAll: false,
        });
      }
    },
    [businessInfo?.id, fetchedMonths, fetchAppointmentsAndTimeBlocks],
  );

  // Unconditionally re-fetch (merge) the month buffer around `date`.
  const refetchMonthBuffer = useCallback(
    async (date: Date) => {
      const { start, end, months } = getMonthBufferDates(date);
      await fetchAppointmentsAndTimeBlocks(start, end, months, {
        replaceAll: false,
      });
    },
    [fetchAppointmentsAndTimeBlocks],
  );

  // Initial fetch on mount - only runs once
  useEffect(() => {
    if (
      !isMissingSalon &&
      businessInfo?.id &&
      !hasInitialFetchCompletedRef.current
    ) {
      performInitialFetch();
    }
  }, [businessInfo?.id, isMissingSalon, performInitialFetch]);

  // Predictive fetch when selectedDate changes (navigation in day/week views)
  useEffect(() => {
    const currentMonth = moment(selectedDate).format("YYYY-MM");

    // Only trigger predictive fetch if user navigated to a new month
    if (currentMonth !== lastSelectedMonth && businessInfo?.id) {
      setLastSelectedMonth(currentMonth);
      performPredictiveFetch(selectedDate);
    }
  }, [
    selectedDate,
    lastSelectedMonth,
    businessInfo?.id,
    performPredictiveFetch,
  ]);

  return {
    isInitialLoading,
    setIsInitialLoading,
    fetchAppointmentsAndTimeBlocks,
    performPredictiveFetch,
    refetchMonthBuffer,
    mapTimeBlocks,
  };
}
